import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import whiteGlobeIcon from '@/features/Company/icons/white-globe-icon.svg'
import whitePhoneIcon from '@/features/Company/icons/white-phone-icon.svg'
import Head from '@/components/head'

import heroBanner from './sterling-karamar-hero-image.jpeg'
import sterlingkaramarQr from './sterling-karamar-qr-code.png'

const SterlingKaramarProfile = ({
  footerOnScreen,
  listingsOnScreen,
  metadata
}) => {
  const { t } = useTranslation('sterlingkaramar')
  const profile = {
    banner: heroBanner.src,
    title: t('sterlingkaramar:Sterling Karamar Property Management'),
    url: '/company/sterlingkaramar'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '375px' }}
        marginBottom="medium"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            alt="Sterling Karamar"
            fill
            priority
            src={heroBanner.src}
            style={{
              objectFit: 'cover',
              objectPosition: '50% 50%'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
          />
          {isDesktop && (
            <Box display="flex" zIndex="dropdown" paddingLeft="gutter">
              <Box style={{ maxWidth: '45%' }}>
                <Stack space="xlarge">
                  <Text size="xlarge" weight="strong" tone="neutralInverted">
                    {t('sterlingkaramar:Sterling Karamar Property Management')}
                  </Text>
                  <Text size="medium" weight="strong" tone="neutralInverted">
                    {t('sterlingkaramar:subtitle')}
                  </Text>
                  <Text size="medium" tone="neutralInverted">
                    {t('sterlingkaramar:description')}
                  </Text>
                  <Text size="medium" tone="neutralInverted">
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap="xxsmall"
                      alignItems="center"
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="xxxsmall"
                      >
                        <Box>
                          <Image
                            src={whitePhoneIcon}
                            alt="White Phone Icon"
                            priority
                            width={16}
                            height={16}
                          />
                        </Box>
                        <Box
                          component="a"
                          href="tel: 416 256 4434"
                          cursor="pointer"
                        >
                          (416) 256-4434
                        </Box>
                      </Box>
                      <Box>|</Box>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="xxxsmall"
                      >
                        <Box>
                          <Image
                            src={whiteGlobeIcon}
                            alt=""
                            priority
                            width={16}
                            height={16}
                          />
                        </Box>
                        <Box
                          component="a"
                          href="https://www.sterlingkaramar.com/"
                          target="_blank"
                          cursor="pointer"
                        >
                          www.sterlingkaramar.com
                        </Box>
                      </Box>
                    </Box>
                  </Text>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {!isDesktop && (
        <Box
          display="flex"
          justifyContent="center"
          width="full"
          marginY="large"
        >
          <Stack space="medium">
            <Box
              display="flex"
              alignItems="center"
              marginRight="large"
              marginX="medium"
            >
              <Heading level="3" weight="weak">
                {t('sterlingkaramar:Sterling Karamar Property Management')}
              </Heading>
            </Box>
            <Box
              display="flex"
              flexDirection={{ mobile: 'column', tablet: 'row' }}
              gap={{ mobile: 'gutter', tablet: 'none' }}
              marginX="medium"
              marginBottom="xlarge"
              marginTop="medium"
              style={{
                maxWidth: '1096px'
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                marginRight="xxlarge"
                style={{
                  width: isMobile ? '100%' : 'fit-content'
                }}
              >
                <Stack space="medium">
                  <Stack space="gutter">
                    <Text size="standard" weight="strong">
                      {t('sterlingkaramar:subtitle')}
                    </Text>
                    <Text size="standard">
                      {t('sterlingkaramar:description')}
                    </Text>
                  </Stack>
                </Stack>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                background="neutralSoft"
                borderRadius="small"
                paddingY="small"
                paddingX="gutter"
                marginTop={{ mobile: 'gutter', tablet: 'none' }}
                style={{
                  height: 'min-content',
                  width: isMobile ? '100%' : 'fit-content'
                }}
              >
                <Stack space="medium">
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={globeIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      href="https://www.sterlingkaramar.com/"
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      www.sterlingkaramar.com
                    </TextLink>
                  </Box>
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={phoneIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      href={`tel: 416 256 4434`}
                      rel="noreferrer noopener"
                    >
                      (416) 256-4434
                    </TextLink>
                  </Box>
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={markerIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      target="_blank"
                      href="https://maps.app.goo.gl/u3wwhLe4PyDF53gr8"
                      rel="noreferrer noopener"
                    >
                      1177 W Hastings St #2033, Vancouver, BC V6E 3T4
                    </TextLink>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>
      )}
      <Box
        textAlign="center"
        paddingBottom="gutter"
        paddingTop={{ mobile: 'none', tablet: 'gutter' }}
      >
        <Heading level="6">{t('sterlingkaramar:Our Portfolio')}</Heading>
      </Box>
      {isDesktop && (
        <Box
          position={footerOnScreen && listingsOnScreen ? 'absolute' : 'fixed'}
          zIndex="dropdown"
          border="borderNeutralLight"
          background="bodyWhite"
          display={{ mobile: 'none', tablet: 'none', desktop: 'flex' }}
          style={{
            bottom: listingsOnScreen && footerOnScreen ? '20px' : null,
            right: '10px'
          }}
          paddingBottom="xsmall"
        >
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            style={{ width: '158px', height: '175px' }}
          >
            <Image
              src={sterlingkaramarQr}
              alt="Sterling Karamar QR Code"
              width={160}
              height={160}
              placeholder="blur"
            />
            <Text align="center" size="xsmall" weight="medium">
              {t('sterlingkaramar:QR description')}
            </Text>
          </Box>
        </Box>
      )}
    </>
  )
}

export default SterlingKaramarProfile
